import { useEffect, useState } from "react";
import {
  TextField,
  IconButton,
  Dialog,
  Typography,
  Box,
  Tooltip,
  Container,
  Stack,
  Button,
  BoxProps,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";

import conf from "../../conf";
import SectionHeader from "./SectionHeader";
import { useProxyKeyEditDescription } from "../../hooks/proxy/keys";
import { useProxyProjectEditDescription } from "../../hooks/proxy/projects";
import { useConfgSysOrganizationEditDescription } from "../../hooks/confg/base";

type DescriptionWithEditProps = {
  id: string;
  type: "project" | "key" | "organization";
  readOnly: boolean;
  description: string;
  alignItems?: BoxProps["alignItems"];
  justifyContent?: BoxProps["justifyContent"];
};

const DescriptionWithEdit = (props: DescriptionWithEditProps) => {
  const {
    id,
    type,
    readOnly,
    description,
    alignItems = "center",
    justifyContent = "space-between",
  } = props;

  const key = useProxyKeyEditDescription();
  const project = useProxyProjectEditDescription();
  const organization = useConfgSysOrganizationEditDescription();

  const [isEditing, setIsEditing] = useState(false);
  const [currentDesc, setCurrentDesc] = useState(description);
  const [updatedDesc, setUpdatedDesc] = useState(currentDesc);

  const handleOpen = () => setIsEditing(true);
  const handleClose = () => setIsEditing(false);

  const handleSave = () => {
    setCurrentDesc(updatedDesc); // save updated description
    setIsEditing(false);

    if (type === "key") {
      key.mutate({ id, description: updatedDesc });
    } else if (type === "project") {
      project.mutate({ id, description: updatedDesc });
    } else if (type === "organization") {
      organization.mutate({ id, description: updatedDesc });
    }
  };

  useEffect(() => {
    setCurrentDesc(description);
    setUpdatedDesc(description);
  }, [description]);

  return (
    <>
      {/* Description and Edit Button */}
      <Box
        display="flex"
        alignItems={alignItems}
        justifyContent={justifyContent}
      >
        <Typography variant="caption" gutterBottom sx={{ display: "block" }}>
          {currentDesc}
        </Typography>

        {!readOnly && (
          <Tooltip title="Edit description">
            <IconButton onClick={handleOpen} color="inherit">
              <EditIcon sx={{ fontSize: 14, p: 0 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Edit Modal */}
      <Dialog fullWidth maxWidth="md" open={isEditing} onClose={handleClose}>
        <Container component="main" sx={conf.STYLE.DIALOG}>
          <SectionHeader
            title="Edit description"
            icon={<EditIcon />}
            buttons={[
              <Button key="exit-desc" color="inherit" onClick={handleClose}>
                <CloseIcon />
              </Button>,
            ]}
          />

          <TextField
            multiline
            fullWidth
            value={updatedDesc}
            variant="outlined"
            onChange={(e) => setUpdatedDesc(e.target.value)}
          />

          <Stack
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Button variant="contained" onClick={handleSave}>
              Save
            </Button>
          </Stack>
        </Container>
      </Dialog>
    </>
  );
};

export default DescriptionWithEdit;
